import * as i0 from '@angular/core';
import { Injectable, Component, ChangeDetectionStrategy, NgModule } from '@angular/core';
import * as i1 from '@spartacus/cart/base/root';
import { CreateCartEvent } from '@spartacus/cart/base/root';
import * as i2 from '@spartacus/core';
import { StorageSyncType, BASE_SITE_CONTEXT_ID, UrlModule, I18nModule, provideDefaultConfig } from '@spartacus/core';
import { of, combineLatest } from 'rxjs';
import { startWith, map, switchMap, distinctUntilChanged, takeWhile } from 'rxjs/operators';
import * as i4 from '@spartacus/storefront';
import { ICON_TYPE, IconModule } from '@spartacus/storefront';
import * as i2$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@angular/router';
import { RouterModule } from '@angular/router';

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const _c0 = a0 => ({
  quantity: a0
});
const _c1 = a0 => ({
  count: a0
});
const _c2 = () => ({
  cxRoute: "cart"
});
const _c3 = a0 => ({
  total: a0
});
function MiniCartComponent_a_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "a", 1);
    i0.ɵɵpipe(1, "cxTranslate");
    i0.ɵɵpipe(2, "cxUrl");
    i0.ɵɵpipe(3, "cxTranslate");
    i0.ɵɵelement(4, "cx-icon", 2);
    i0.ɵɵelementStart(5, "span", 3);
    i0.ɵɵtext(6);
    i0.ɵɵpipe(7, "async");
    i0.ɵɵpipe(8, "cxTranslate");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(9, "span", 4);
    i0.ɵɵtext(10);
    i0.ɵɵpipe(11, "async");
    i0.ɵɵpipe(12, "cxTranslate");
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const data_r1 = ctx.ngIf;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("title", i0.ɵɵpipeBind2(1, 6, "miniCart.item", i0.ɵɵpureFunction1(24, _c1, data_r1.quantity)))("routerLink", i0.ɵɵpipeBind1(2, 9, i0.ɵɵpureFunction0(26, _c2)));
    i0.ɵɵattribute("aria-label", i0.ɵɵpipeBind2(3, 11, "miniCart.item", i0.ɵɵpureFunction1(27, _c1, data_r1.quantity)));
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("type", ctx_r1.iconTypes.CART);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(8, 16, "miniCart.total", i0.ɵɵpureFunction1(29, _c3, i0.ɵɵpipeBind1(7, 14, ctx_r1.total$))), " ");
    i0.ɵɵadvance(4);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(12, 21, "miniCart.count", i0.ɵɵpureFunction1(31, _c1, i0.ɵɵpipeBind1(11, 19, ctx_r1.quantity$))), " ");
  }
}
let MiniCartComponentService = /*#__PURE__*/(() => {
  class MiniCartComponentService {
    constructor(activeCartFacade, authService, statePersistenceService, siteContextParamsService, eventService) {
      this.activeCartFacade = activeCartFacade;
      this.authService = authService;
      this.statePersistenceService = statePersistenceService;
      this.siteContextParamsService = siteContextParamsService;
      this.eventService = eventService;
    }
    /**
     * This function supports lazy loading of the cart functionality's code. We only call
     * the activeCartFacade if we know there is actually a cart.
     * Without a cart, we can return a default value and
     * avoid loading the cart library code.
     */
    getQuantity() {
      return this.activeCartRequired().pipe(switchMap(activeCartRequired => {
        if (activeCartRequired) {
          return this.activeCartFacade.getActive().pipe(startWith({
            totalUnitCount: 0
          }), map(cart => cart.totalUnitCount || 0));
        } else {
          return of(0);
        }
      }));
    }
    /**
     * This function supports lazy loading of the cart functionality's code. We only call
     * the activeCartFacade if we know there is actually a cart.
     * Without a cart, we can return a default value and
     * avoid loading the cart library code.
     */
    getTotalPrice() {
      return this.activeCartRequired().pipe(switchMap(activeCartRequired => {
        if (activeCartRequired) {
          return this.activeCartFacade.getActive().pipe(map(cart => cart.totalPrice?.formattedValue ?? ''));
        } else {
          return of('');
        }
      }));
    }
    /**
     * This function determines if it is required to get active cart data from ActiveCartFacade.
     * It is required to call the ActiveCartFacade if one of these criteria is met:
     * - There is an active cart id in the browser local storage
     * - A user is authenticated
     * - The cart library code chunk with the ActiveCartFacade implementation is already loaded.
     *
     * Once the observable returned by activeCartRequired emits true, it completes.
     * activeCartRequired helps to make the mini cart compatible with some level of lazy loading.
     */
    activeCartRequired() {
      return combineLatest([this.hasActiveCartInStorage(), this.authService.isUserLoggedIn(), this.isCartCreated()]).pipe(map(([hasCartInStorage, isUserLoggedIn, isCartCreated]) => hasCartInStorage || isUserLoggedIn || isCartCreated), distinctUntilChanged(), takeWhile(hasCart => !hasCart, true));
    }
    hasActiveCartInStorage() {
      return this.getCartStateFromBrowserStorage().pipe(map(state => Boolean(state?.active)));
    }
    isCartCreated() {
      return this.eventService.get(CreateCartEvent).pipe(map(_ => true), startWith(false));
    }
    getCartStateFromBrowserStorage() {
      return this.siteContextParamsService.getValues([BASE_SITE_CONTEXT_ID]).pipe(map(context => {
        return this.statePersistenceService.readStateFromStorage({
          key: 'cart',
          context: context,
          storageType: StorageSyncType.LOCAL_STORAGE
        });
      }));
    }
    static {
      this.ɵfac = function MiniCartComponentService_Factory(t) {
        return new (t || MiniCartComponentService)(i0.ɵɵinject(i1.ActiveCartFacade), i0.ɵɵinject(i2.AuthService), i0.ɵɵinject(i2.StatePersistenceService), i0.ɵɵinject(i2.SiteContextParamsService), i0.ɵɵinject(i2.EventService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MiniCartComponentService,
        factory: MiniCartComponentService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MiniCartComponentService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let MiniCartComponent = /*#__PURE__*/(() => {
  class MiniCartComponent {
    constructor(miniCartComponentService) {
      this.miniCartComponentService = miniCartComponentService;
      this.iconTypes = ICON_TYPE;
      this.quantity$ = this.miniCartComponentService.getQuantity();
      this.total$ = this.miniCartComponentService.getTotalPrice();
    }
    static {
      this.ɵfac = function MiniCartComponent_Factory(t) {
        return new (t || MiniCartComponent)(i0.ɵɵdirectiveInject(MiniCartComponentService));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: MiniCartComponent,
        selectors: [["cx-mini-cart"]],
        decls: 2,
        vars: 5,
        consts: [[3, "title", "routerLink", 4, "ngIf"], [3, "title", "routerLink"], [3, "type"], [1, "total"], [1, "count"]],
        template: function MiniCartComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, MiniCartComponent_a_0_Template, 13, 33, "a", 0);
            i0.ɵɵpipe(1, "async");
          }
          if (rf & 2) {
            i0.ɵɵproperty("ngIf", i0.ɵɵpureFunction1(3, _c0, i0.ɵɵpipeBind1(1, 1, ctx.quantity$)));
          }
        },
        dependencies: [i2$1.NgIf, i3.RouterLink, i4.IconComponent, i2$1.AsyncPipe, i2.UrlPipe, i2.TranslatePipe],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return MiniCartComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let MiniCartModule = /*#__PURE__*/(() => {
  class MiniCartModule {
    static {
      this.ɵfac = function MiniCartModule_Factory(t) {
        return new (t || MiniCartModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: MiniCartModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [provideDefaultConfig({
          cmsComponents: {
            MiniCartComponent: {
              component: MiniCartComponent
            }
          }
        })],
        imports: [CommonModule, RouterModule, UrlModule, IconModule, I18nModule]
      });
    }
  }
  return MiniCartModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Generated bundle index. Do not edit.
 */

export { MiniCartComponent, MiniCartComponentService, MiniCartModule };
